import { useQueries } from "@tanstack/react-query";
import { useTenant } from "../../contexts/TenantContext";
import { formatPlaceholders } from "../../utils/formatText";
import { getConfig } from "../../config/environment";

interface MLSComplianceResponse {
  status: string;
  data: {
    mlsBoardCode: string;
    mlsLogo: string;
    complianceText: string;
  };
}

const fetchMLSCompliance = async (
  mlsBoardCode: string,
  tenantName: string,
  brokerageName: string
): Promise<MLSComplianceResponse> => {
  const response = await fetch(
    `${getConfig().cloudRunServices.mls}/access`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ mlsBoardCode }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch MLS compliance data");
  }

  const data = await response.json();

  // Format placeholders in compliance text
  if (data.status === "success" && data.data.complianceText) {
    data.data.complianceText = formatPlaceholders(data.data.complianceText, {
      "{tenant_name}": tenantName,
      "{brokerage}": brokerageName,
      "{dmca_agent_name}": "Kyle Fedewa",
      "{dmca_agent_email}":
        "<a href='mailto:kyle@thespeculogroup.com' class='text-primary underline'>kyle@thespeculogroup.com</a>",
      "{dmca_agent_contact_no}":
        "<a href='tel:+19375104461' class='text-primary underline'>+1 (937) 510-4461</a>",
      "{dmca_mailing_address}": "7972 Southbury Dr Dayton, OH 45458",
    });
  }

  return data;
};

export const useMLSCompliance = (mlsBoardCodes: string[]) => {
  const { config } = useTenant();
  const tenantName = config?.orgData?.org_name || "";
  const brokerageName = config?.branding?.brokerageName || config?.orgData?.brokerage_name || "";

  return useQueries({
    queries: mlsBoardCodes.map((mlsBoardCode) => ({
      queryKey: ["mlsCompliance", mlsBoardCode],
      queryFn: () =>
        fetchMLSCompliance(mlsBoardCode, tenantName, brokerageName),
      enabled: !!mlsBoardCode,
      staleTime: Infinity, // Data will never become stale
      gcTime: 1000 * 60 * 60 * 24 * 30, // Keep unused data in cache for 30 days
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    })),
  });
};
